import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Power, PowerOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import PageShell from "@/components/masters/PageShell";
import { useAuth } from "@/context/AuthContext";
import {
  Field,
  GhostButton,
  OutlineButton,
  PrimaryButton,
  SearchBox,
  StatusBadge,
  inputCls,
  selectCls,
} from "@/components/masters/UI";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const empty = { location_id: "", code: "", name: "", capacity: 1, status: "active" };

const DepartmentsPage = () => {
  const { user, isLocationAdmin, selectedLocationId } = useAuth();
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [q, setQ] = useState("");
  const [locFilter, setLocFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (locFilter && !isLocationAdmin) params.location_id = locFilter;
      const promises = [api.get("/departments", { params })];
      if (!isLocationAdmin) {
        promises.push(api.get("/locations", { params: { only_active: true } }));
      }
      const [depsRes, locsRes] = await Promise.all(promises);
      setRows(depsRes.data);
      if (!isLocationAdmin && locsRes) {
        setLocations(locsRes.data);
      } else if (user?.location_id) {
        setLocations([{ id: user.location_id, name: user.location_name || "Assigned Location" }]);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, locFilter, selectedLocationId, isLocationAdmin, user]);

  useEffect(() => {
    load();
  }, [load]);

  const locName = useMemo(() => {
    const m = new Map(locations.map((l) => [l.id, l.name]));
    return (id) => m.get(id) || user?.location_name || "—";
  }, [locations, user]);

  const startNew = () => {
    setEditing(null);
    setForm({ ...empty, location_id: isLocationAdmin ? "" : (locations[0]?.id || "") });
    setErr(null);
    setOpen(true);
  };

  const startEdit = (row) => {
    setEditing(row);
    setForm({
      location_id: isLocationAdmin ? "" : row.location_id,
      code: row.code,
      name: row.name,
      capacity: row.capacity,
      status: row.status,
    });
    setErr(null);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!isLocationAdmin && !form.location_id) return setErr("Location is required.");
    if (!form.code.trim() || !form.name.trim()) return setErr("Code and Name are required.");
    const cap = Number(form.capacity);
    if (!Number.isFinite(cap) || cap < 1) return setErr("Capacity must be a positive integer.");

    setSaving(true);
    try {
      const body = { ...form, capacity: Math.floor(cap) };
      if (isLocationAdmin) {
        delete body.location_id;
      }
      if (editing) {
        await api.put(`/departments/${editing.id}`, body);
        toast.success("Department updated");
      } else {
        await api.post("/departments", body);
        toast.success("Department created");
      }
      setOpen(false);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    const next = row.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/departments/${row.id}/status`, { status: next });
      toast.success(next === "active" ? "Activated" : "Inactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const noLocations = locations.length === 0;

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="departments-page">
      <AppHeader />
      <PageShell
        eyebrow="Masters · Departments"
        title="Departments"
        subtitle="Grouping within a Location. Capacity is shown for reference — never enforced."
        primaryAction={
          <PrimaryButton testId="dep-add" onClick={startNew} disabled={noLocations}>
            <Plus className="mr-1 inline h-4 w-4" /> Add Department
          </PrimaryButton>
        }
        filters={
          <>
            <SearchBox value={q} onChange={setQ} placeholder="Search code or name…" testId="dep-search" />
            {!isLocationAdmin && (
              <select
                className={selectCls + " w-56"}
                data-testid="dep-filter-location"
                value={locFilter}
                onChange={(e) => setLocFilter(e.target.value)}
              >
                <option value="">All locations</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            )}
          </>
        }
        testId="departments-shell"
      >
        {noLocations && (
          <div
            className="mb-4 rounded-2xl border p-4 text-sm"
            style={{
              backgroundColor: "rgba(242, 153, 74, 0.10)",
              borderColor: "rgba(242, 153, 74, 0.35)",
              color: "#7a3f0a",
            }}
            data-testid="dep-no-locations"
          >
            No active Locations yet. Create one first in <b>Masters → Locations</b>.
          </div>
        )}

        <div
          className="overflow-hidden rounded-2xl border bg-white shadow-card"
          style={{ borderColor: "rgba(52,73,94,0.08)" }}
        >
          <div className="hidden grid-cols-[1fr_1.5fr_1.5fr_100px_120px_140px] gap-4 bg-[color:var(--vision-ice)] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-vision-slate/60 md:grid">
            <div>Code</div>
            <div>Name</div>
            <div>Location</div>
            <div>Capacity</div>
            <div>Status</div>
            <div className="text-right">Actions</div>
          </div>

          {loading && (
            <div className="px-5 py-10 text-center text-sm text-vision-slate/60" data-testid="dep-loading">
              Loading…
            </div>
          )}

          {!loading && rows.length === 0 && (
            <div className="px-5 py-14 text-center text-sm text-vision-slate/60" data-testid="dep-empty">
              No departments found.
            </div>
          )}

          {!loading &&
            rows.map((row) => (
              <div
                key={row.id}
                data-testid={`dep-row-${row.id}`}
                className="grid grid-cols-1 items-center gap-3 border-t px-5 py-4 md:grid-cols-[1fr_1.5fr_1.5fr_100px_120px_140px] md:gap-4"
                style={{ borderColor: "rgba(52,73,94,0.08)" }}
              >
                <div className="font-display text-sm font-bold text-vision-ink">{row.code}</div>
                <div className="text-sm text-vision-slate">{row.name}</div>
                <div className="text-sm text-vision-slate">{locName(row.location_id)}</div>
                <div className="font-display text-sm font-bold text-vision-ink numeric-tabular">{row.capacity}</div>
                <div><StatusBadge active={row.status === "active"} /></div>
                <div className="flex items-center justify-end gap-2">
                  <GhostButton testId={`dep-edit-${row.id}`} onClick={() => startEdit(row)}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </GhostButton>
                  <GhostButton testId={`dep-toggle-${row.id}`} onClick={() => toggleStatus(row)}>
                    {row.status === "active"
                      ? (<><PowerOff className="h-3.5 w-3.5" /> Inactivate</>)
                      : (<><Power className="h-3.5 w-3.5" /> Activate</>)}
                  </GhostButton>
                </div>
              </div>
            ))}
        </div>
      </PageShell>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl" data-testid="dep-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-vision-ink">
              {editing ? "Edit Department" : "Add Department"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={submit} className="mt-2 grid gap-4">
            <Field label="Location">
              {isLocationAdmin ? (
                <input
                  className={inputCls}
                  disabled
                  value={user?.location_name || "Assigned Location"}
                />
              ) : (
                <select
                  className={selectCls}
                  data-testid="dep-form-location"
                  value={form.location_id}
                  onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                >
                  <option value="">Select a location…</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              )}
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Code">
                <input
                  className={inputCls}
                  data-testid="dep-form-code"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g. HK"
                />
              </Field>
              <Field label="Capacity" hint="Reference count — never enforced.">
                <input
                  className={inputCls}
                  type="number"
                  min={1}
                  data-testid="dep-form-capacity"
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                />
              </Field>
            </div>

            <Field label="Name">
              <input
                className={inputCls}
                data-testid="dep-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Housekeeping"
              />
            </Field>

            <Field label="Status">
              <select
                className={selectCls}
                data-testid="dep-form-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>

            {err && (
              <div
                data-testid="dep-form-error"
                className="rounded-xl border px-4 py-3 text-sm"
                style={{
                  backgroundColor: "rgba(231, 76, 60, 0.08)",
                  borderColor: "rgba(231, 76, 60, 0.3)",
                  color: "var(--vision-error)",
                }}
              >
                {err}
              </div>
            )}

            <DialogFooter className="mt-2 gap-2 sm:justify-end">
              <OutlineButton type="button" testId="dep-form-cancel" onClick={() => setOpen(false)}>
                Cancel
              </OutlineButton>
              <PrimaryButton type="submit" testId="dep-form-submit" disabled={saving}>
                {saving ? "Saving…" : editing ? "Save changes" : "Create"}
              </PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepartmentsPage;
